import { useState } from 'react';
import { useNotifications } from '@toolpad/core/useNotifications';
import { useSession } from '@toolpad/core';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { EventDateCheck } from 'shared/types/appTypes';
import { db } from '../firebase/firebaseConfig';
import { queryClient } from '../utils/queryClient';

/** React Query key for the All Events acknowledgement read; invalidated after each acknowledgement. */
export const DATE_CHECK_ACKS_QUERY_KEY = ['events', 'dateCheckAcks'] as const;

/**
 * Staff acknowledgement of a date check warning. Writes only `dateCheckAck` (merge),
 * outside useSaveEvent and without `updatedAt`, so it never requeues the event for sync.
 * The acknowledgement holds only while status, titleDate and forecastedDate still match.
 */
export default function useAcknowledgeEventDate() {
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const notifications = useNotifications();
  const { user } = useSession() || {};

  const acknowledge = async (
    eventId: string,
    check: Pick<EventDateCheck, 'status' | 'titleDate' | 'forecastedDate'>
  ) => {
    if (!eventId) return;

    setIsAcknowledging(true);
    try {
      await setDoc(
        doc(db, 'events', eventId),
        {
          dateCheckAck: {
            status: check.status,
            titleDate: check.titleDate,
            forecastedDate: check.forecastedDate,
            by: user?.name || user?.email || 'Unknown',
            at: serverTimestamp()
          }
        },
        { merge: true }
      );
      // All Events caches acknowledgements for 5 minutes; mark them stale so the next view re-reads.
      await queryClient.invalidateQueries({ queryKey: DATE_CHECK_ACKS_QUERY_KEY });
    } catch (error) {
      console.error('Error acknowledging event date check:', error);
      notifications.show('Could not save the acknowledgement. Please try again.', {
        severity: 'error',
        autoHideDuration: 3000
      });
    } finally {
      setIsAcknowledging(false);
    }
  };

  return { acknowledge, isAcknowledging };
}
